import type { EntitlementItem } from "~/features/entitlements/types"
import { keysFor } from "~/features/entitlements/windows"
import { ebookHref } from "~/features/library/projections"
import type { EbookAsset } from "~/features/library/types"
import { EntitlementCategory } from "~/graphql/generated/graphql"
import type { StudyEbookIndex, StudyEbookLookup, StudyMaterial } from "./types"

/**
 * Which ebook accompanies a study.
 *
 * The graph says nothing about this. `Study` and `Ebook` are unrelated `Node`s
 * with no shared field beyond `lin`, which no deployed graph serves — so the
 * pairing is a title-stem heuristic over the SAME `keysFor` the commerce seat
 * match uses, reused rather than forked so stemming has one definition.
 *
 * It is annotation, never gating. A study that has a book we failed to find must
 * look exactly like a study that has no book, and nothing here removes an ebook
 * from the section a customer would look in.
 */

const AMBIGUOUS = Symbol("ambiguous")

type Slot = StudyMaterial | typeof AMBIGUOUS

/**
 * Two entries under one key are one answer only when they open the same thing.
 * Otherwise neither is offered: a chip that opens the wrong book is
 * indistinguishable from one that opens the right one until it is being read.
 */
const claim = (index: Map<string, Slot>, key: string, material: StudyMaterial): void => {
  const existing = index.get(key)

  if (existing === undefined) {
    index.set(key, material)
  } else if (existing !== AMBIGUOUS && existing.href !== material.href) {
    index.set(key, AMBIGUOUS)
  }
}

/** An ebook with no title has no key to be found by, and one with no id has nothing to open. */
const materialFromAsset = (asset: EbookAsset): StudyMaterial | undefined => {
  const link = ebookHref({ id: asset.id, lin: undefined })

  return asset.title && link.href && link.linkIdKind
    ? {
        title: asset.title,
        href: link.href,
        linkIdKind: link.linkIdKind,
        coverUrl: asset.cover ?? undefined,
        source: "dce",
      }
    : undefined
}

/**
 * A seat is worth pairing only if it is an ebook and is openable now.
 *
 * `orphanEntitlements.ts` renders a lapsed seat as a dead grey card in the Ebooks
 * section, which is honest there — that section is a record of what the customer
 * has. A material on a study is not a record, it is an invitation, so an ended or
 * not-yet-started window offers nothing.
 */
const isPairable = (item: EntitlementItem): boolean =>
  item.category === EntitlementCategory.Ebook && item.window.state === "active"

/** A seat can only be opened by its item number, the same identifier an orphan ebook card uses. */
const materialFromSeat = (item: EntitlementItem): StudyMaterial | undefined => {
  const link = ebookHref({ id: undefined, lin: item.itemNumber || undefined })

  return link.href && link.linkIdKind
    ? { title: item.title, href: link.href, linkIdKind: link.linkIdKind, coverUrl: item.imageUrl, source: "mlc" }
    : undefined
}

export const buildStudyEbookIndex = (
  ebooks: readonly EbookAsset[],
  seats: readonly EntitlementItem[],
): StudyEbookIndex => {
  const byKey = new Map<string, Slot>()

  for (const asset of ebooks) {
    const material = materialFromAsset(asset)

    if (material) {
      for (const key of keysFor(material.title)) {
        claim(byKey, key, material)
      }
    }
  }

  /**
   * Seats fill only the keys the graph left empty. An `Ebook` asset carries a
   * cover and the aggregate id every other reader link in the app is built from,
   * so it is the better record wherever it exists.
   */
  for (const item of seats.filter(isPairable)) {
    const material = materialFromSeat(item)

    if (material) {
      for (const key of keysFor(material.title)) {
        if (!byKey.has(key)) {
          claim(byKey, key, material)
        }
      }
    }
  }

  const ambiguousKeys = [...byKey.entries()].filter(([, slot]) => slot === AMBIGUOUS).map(([key]) => key)

  const resolve = (studyTitle: string): StudyEbookLookup => {
    if (ebooks.length === 0 && seats.length === 0) {
      return { status: "inactive" }
    }

    const keys = keysFor(studyTitle)

    for (const key of keys) {
      const found = byKey.get(key)

      if (found !== undefined && found !== AMBIGUOUS) {
        return { status: "matched", material: found }
      }
    }

    const blocked = keys.filter(key => byKey.get(key) === AMBIGUOUS)

    return blocked.length > 0 ? { status: "ambiguous", keys: blocked } : { status: "unmatched" }
  }

  return {
    lookup: studyTitle => {
      const found = resolve(studyTitle)

      return found.status === "matched" ? found.material : undefined
    },
    resolve,
    ambiguousKeys,
  }
}

export const EMPTY_STUDY_EBOOK_INDEX: StudyEbookIndex = {
  lookup: () => undefined,
  resolve: () => ({ status: "inactive" }),
  ambiguousKeys: [],
}
